<template>
  <SectionShell id="kit" num="02" kicker="The Kit">
    <template #title>Eight objects. <span class="sig">No talent</span><br />required, but the right<br />tools are not optional.</template>
    <template #lede>
      <p>
        This is a cheap discipline — the whole kit costs less than a night out. But two
        of these items do most of the work, and beginners almost never own them. If you
        blend with your fingertip on printer paper, no amount of practice will save the
        drawing.
      </p>
    </template>

    <ul class="kit">
      <li
        v-for="(k, i) in kit"
        :key="k.name"
        class="kit__item card"
        :class="{ 'is-key': k.key }"
        v-reveal="i * 60"
      >
        <div class="kit__icon" v-html="k.icon" aria-hidden="true"></div>
        <div class="kit__body">
          <span class="kit__idx mono">{{ String(i + 1).padStart(2, '0') }}</span>
          <h3>{{ k.name }}</h3>
          <p class="kit__spec mono">{{ k.spec }}</p>
          <p class="kit__why">{{ k.why }}</p>
        </div>
        <span v-if="k.key" class="kit__flag mono">does the heavy lifting</span>
      </li>
    </ul>

    <div class="notes">
      <div class="note card" v-reveal>
        <h4 class="mono">How to hold a tortillion</h4>
        <p>
          At a shallow angle, never straight down — a flattened tip smears in blotches.
          Keep a dirty one for shadow work and a clean one for anything approaching the
          light side. Dragging graphite from a dark area into a highlight is the single
          most common way to ruin an otherwise good face.
        </p>
      </div>
      <div class="note card" v-reveal="120">
        <h4 class="mono">What you don't need</h4>
        <p>
          A range of pencils from 6H to 8B. A blending stump the size of a cigar. Fixative.
          Expensive paper. A drawing board that tilts. All of it is fine, none of it is the
          reason a portrait looks real — the five-value scale is.
        </p>
      </div>
    </div>
  </SectionShell>
</template>

<script setup>
import SectionShell from '../ui/SectionShell.vue'

const S = 'stroke="currentColor" fill="none" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"'

const kit = [
  {
    name: 'Smooth bristol',
    spec: 'plate / smooth surface · 250 gsm',
    why: 'Graphite has to slide, not catch. Toothy sketch paper traps particles in its pits and every blend comes out speckled instead of silky.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><path d="M12 8h18l6 6v26H12z"/><path d="M30 8v6h6"/><path d="M17 22h14M17 28h14M17 34h9"/></g></svg>`
  },
  {
    name: '2B mechanical pencil',
    spec: '0.5 mm lead · 2B grade',
    why: 'Constant line width, no sharpening, no shifting weight in the hand. 2B is soft enough to reach near-black and hard enough not to smear on contact.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><path d="M28 6l14 14-20 20-9 3 3-9z"/><path d="M25 9l14 14"/><path d="M15 34l5 5"/></g></svg>`
  },
  {
    name: 'Tortillions',
    spec: 'rolled paper stumps · buy ten',
    key: true,
    why: 'The actual secret. A tightly rolled paper cone that pushes graphite into the paper evenly. Your finger deposits oil and produces grey mush; a tortillion produces skin.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><path d="M20 6h8l4 30-8 6-8-6z"/><path d="M20 6l8 0"/><path d="M18 26h12"/></g></svg>`
  },
  {
    name: 'Kneaded eraser',
    spec: 'grey putty · never crumbs',
    key: true,
    why: 'Not a correction tool — a drawing tool. Pinch it to a point to lift a catchlight, flatten it to a razor edge to pull strands of hair out of a dark mass.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><path d="M10 30c0-8 6-14 14-14s14 5 14 13c0 6-5 9-10 9s-6-3-11-3-7 0-7-5z"/><path d="M18 26c2-2 5-3 8-2"/></g></svg>`
  },
  {
    name: 'Stick eraser',
    spec: 'retractable, hard vinyl',
    why: 'For the jobs putty cannot do: cutting a crisp edge back into a soft area, clearing a grid line, taking out a stubborn dark in a tight corner.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><rect x="20" y="6" width="8" height="26" rx="2"/><path d="M20 32h8l2 8H18z"/><path d="M20 14h8"/></g></svg>`
  },
  {
    name: 'Circle template',
    spec: 'plastic stencil, 2–20 mm',
    why: 'Irises and pupils are circles. A hand-drawn wobbly circle is the fastest way to make a portrait look wrong, and the two eyes must use the exact same hole.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><rect x="6" y="10" width="36" height="28" rx="2"/><circle cx="17" cy="24" r="6"/><circle cx="30" cy="21" r="4"/><circle cx="34" cy="31" r="2.5"/></g></svg>`
  },
  {
    name: 'Acetate + fine marker',
    spec: 'clear report covers · ruler',
    why: 'Rule 25 mm and 12 mm squares onto clear sheets once and you own reusable grid overlays forever. Slide any photograph underneath and it becomes a shape puzzle.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><rect x="8" y="8" width="32" height="32" rx="1"/><path d="M18 8v32M29 8v32M8 18h32M8 29h32"/></g></svg>`
  },
  {
    name: 'A hard-lit reference',
    spec: 'one dominant light source',
    why: 'Your drawing can only be as good as the photo. Look for one clearly light side and one clearly dark side. Flat, even lighting gives you nothing to draw.',
    icon: `<svg viewBox="0 0 48 48"><g ${S}><circle cx="24" cy="24" r="11"/><path d="M24 5v5M24 38v5M5 24h5M38 24h5M11 11l3.5 3.5M33.5 33.5L37 37M37 11l-3.5 3.5M14.5 33.5L11 37"/></g></svg>`
  }
]
</script>

<style scoped>
.kit {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(258px, 1fr));
  gap: 1px;
  background: var(--edge-soft);
  border: 1px solid var(--edge-soft);
}
.kit__item {
  position: relative;
  border: 0;
  border-radius: 0;
  padding: 28px 24px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: rgba(9, 13, 19, 0.72);
  transition: background 0.35s, transform 0.35s;
}
.kit__item:hover { background: rgba(18, 26, 36, 0.9); }
.kit__item.is-key { background: rgba(20, 16, 10, 0.75); }
.kit__item.is-key:hover { background: rgba(30, 23, 12, 0.9); }

.kit__icon { color: var(--v3); width: 44px; height: 44px; transition: color 0.35s; }
.kit__icon :deep(svg) { width: 100%; height: 100%; }
.kit__item:hover .kit__icon { color: var(--signal); }
.kit__item.is-key .kit__icon { color: var(--light); }

.kit__idx { color: var(--text-faint); font-size: 9px; display: block; margin-bottom: 8px; }
.kit__body h3 { font-size: 19px; font-weight: 500; margin-bottom: 6px; }
.kit__spec {
  font-size: 9px;
  letter-spacing: 0.12em;
  color: var(--signal);
  text-transform: none;
  margin: 0 0 12px;
  opacity: 0.85;
}
.kit__item.is-key .kit__spec { color: var(--light); }
.kit__why { color: var(--text-dim); font-size: 14px; line-height: 1.62; margin: 0; }

.kit__flag {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 8px;
  letter-spacing: 0.16em;
  color: var(--ink-900);
  background: var(--light);
  padding: 4px 9px;
}

.notes {
  margin-top: 1px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1px;
}
.note { border-radius: 0; padding: 26px 24px; }
.note h4 { color: var(--signal); font-size: 10px; margin-bottom: 12px; font-weight: 400; }
.note p { color: var(--text-dim); font-size: 14px; margin: 0; }
</style>
